'use client';

import { useEffect, useRef, useState } from 'react';
import { getSession } from 'next-auth/react';
import LoginForm from './LoginForm';
import LoginStatus from './LoginStatus';

const KEY_PREFIX = 'ent_login_session_refresh:';
// Longer than one attempt can take (the backend exchange times out at 15s),
// short enough that a heal that worked never blocks the next expiry.
const ATTEMPT_WINDOW_MS = 5 * 60 * 1000;

// True when this URL may try one refresh. A recent attempt for the same URL
// means it did not heal, so the key is consumed and the form shows instead of
// looping. Without storage the attempt could not be bounded, so no attempt.
function claimRefresh(key: string): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(key));
    if (last && Date.now() - last < ATTEMPT_WINDOW_MS) {
      window.sessionStorage.removeItem(key);
      return false;
    }
    window.sessionStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/**
 * Heals an expired backend JWT on /login without a click.
 *
 * A Server Component cannot write cookies (next/dist/docs cookies.md, "Setting
 * cookies is not supported during Server Component rendering"), so the
 * browser asks for the session instead: GET /api/auth/session runs the jwt
 * callback, which re-exchanges the backend JWT, and writes the new cookie
 * (next-auth core/routes/session.js). The page then reloads so the /login
 * server gate re-runs with it. A full reload, not router.refresh(): if the
 * re-exchange failed the server renders this same component, React would keep
 * the mounted instance, and its effect would never run again to show the form.
 */
export default function SessionRefresh({
  callbackUrl,
}: {
  callbackUrl?: string;
}) {
  const [showForm, setShowForm] = useState(false);
  // StrictMode runs this effect twice in dev; a second run would consume the
  // key the first wrote while the first still reloads, looping a failed heal.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const { pathname, search } = window.location;
    if (!claimRefresh(`${KEY_PREFIX}${pathname}${search}`)) {
      setShowForm(true);
      return;
    }
    // getSession never throws (a failed fetch resolves null); reload either
    // way and let the server gate decide.
    void getSession({ broadcast: false }).then(() => window.location.reload());
  }, []);

  if (showForm) return <LoginForm callbackUrl={callbackUrl} />;
  return <LoginStatus />;
}
